/**
 * Reading a JWT's payload on the client.
 *
 * Nothing here verifies a signature, and nothing read here may decide what the
 * user is allowed to do: the backend checks every token it receives. The client
 * only uses these claims for bookkeeping, such as working out when to renew.
 */

/** Decode the payload section of a JWT (base64url → JSON). `null` on any parse failure. */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
    const payload: unknown = JSON.parse(atob(padded));
    return payload && typeof payload === "object" ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

/** The access-token claims session renewal needs, with times in epoch milliseconds. */
export interface AccessTokenClaims {
  issuedAtMs: number | null;
  expiresAtMs: number | null;
  /** "tenant" or "platform" (see api-service briefs/auth_design.md §4). */
  typ: string | null;
  tenantId: string | null;
  /** The tenant membership (`users.id`) the token acts through; records authored in the tenant name it. */
  membershipId: string | null;
  /** Set when a platform superadmin is acting as this membership. */
  impersonatedBy: string | null;
}

const readSeconds = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value * 1000 : null;

const readString = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

/** The renewal-relevant claims of `token`, or `null` when it is not a readable JWT. */
export const readAccessTokenClaims = (token: string): AccessTokenClaims | null => {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return {
    issuedAtMs: readSeconds(payload.iat),
    expiresAtMs: readSeconds(payload.exp),
    typ: readString(payload.typ),
    tenantId: readString(payload.tid),
    membershipId: readString(payload.mid),
    impersonatedBy: readString(payload.imp),
  };
};
